import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Heart,
  ShoppingCart,
  Minus,
  Plus,
  Truck,
  ShieldCheck,
  RotateCcw,
  Star,
  Home,
  Share2,
  Check,
} from 'lucide-react';
import { products } from '../mock';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../lib/format';
import ProductCard from '../components/ProductCard';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { products: apiProducts } = useProducts();
  const product = apiProducts.find((p) => p.slug === slug) || products.find((p) => p.slug === slug);
  const { addToCart, toggleFavorite, favorites } = useCart();
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('desc');

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-neutral-900">Produsul nu a fost găsit</h1>
        <button onClick={() => navigate('/magazin')} className="mt-6 px-6 py-3 bg-amber-500 text-white rounded-full">
          Înapoi la magazin
        </button>
      </div>
    );
  }

  const isFav = favorites.includes(product.id);
  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm flex items-center gap-2 text-neutral-500">
          <Link to="/" className="hover:text-amber-600 flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Acasă
          </Link>
          <span>/</span>
          <Link to={`/categorie/${product.category}`} className="hover:text-amber-600">{product.categoryName}</Link>
          <span>/</span>
          <span className="text-neutral-900 line-clamp-1">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-square bg-neutral-50 rounded-2xl overflow-hidden border border-neutral-200 zoom-img">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="mt-4 flex gap-3">
              {product.images.map((img, i) => (
                <div key={i} className="w-20 h-20 rounded-lg overflow-hidden border-2 border-amber-500">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            {product.discount > 0 && (
              <span className="inline-block bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded mb-3">
                Reducere -{product.discount}%
              </span>
            )}
            <Link to={`/categorie/${product.category}`} className="text-xs uppercase tracking-[0.2em] text-amber-700 hover:underline">
              {product.categoryName}
            </Link>
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-neutral-900 mt-2">
              {product.name}
            </h1>

            <div className="mt-3 flex items-center gap-3">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className="w-4 h-4 text-neutral-300" />
                ))}
              </div>
              <span className="text-xs text-neutral-500">Evaluat la 0 din 5</span>
              <span className="text-xs text-neutral-400">|</span>
              <span className="text-xs text-neutral-500">SKU: {product.sku}</span>
            </div>

            <div className="mt-5 flex items-end gap-3">
              {product.oldPrice && (
                <span className="text-lg text-neutral-400 line-through">{formatPrice(product.oldPrice)}</span>
              )}
              <span className="text-3xl md:text-4xl font-bold text-neutral-900">{formatPrice(product.price)}</span>
            </div>

            {product.inStock && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                <Check className="w-4 h-4" /> Disponibil în stoc
              </div>
            )}

            <p className="mt-5 text-neutral-600 leading-relaxed">{product.description}</p>

            <div className="mt-6 flex flex-wrap items-stretch gap-3">
              <div className="flex items-center border border-neutral-300 rounded-full">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 flex items-center justify-center hover:bg-neutral-50 rounded-l-full">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center font-medium">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-12 flex items-center justify-center hover:bg-neutral-50 rounded-r-full">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => addToCart(product, qty)}
                className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 h-12 rounded-full transition-colors"
              >
                <ShoppingCart className="w-5 h-5" /> Adaugă în coș
              </button>
              <button
                onClick={() => toggleFavorite(product.id)}
                className={`w-12 h-12 inline-flex items-center justify-center rounded-full border transition-colors ${
                  isFav
                    ? 'bg-amber-500 border-amber-500 text-white'
                    : 'bg-white border-neutral-300 text-neutral-700 hover:border-amber-500 hover:text-amber-600'
                }`}
              >
                <Heart className={`w-5 h-5 ${isFav ? 'fill-current' : ''}`} />
              </button>
              <button className="w-12 h-12 inline-flex items-center justify-center rounded-full border border-neutral-300 text-neutral-700 hover:border-amber-500 hover:text-amber-600 transition-colors">
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Trust badges */}
            <div className="mt-8 grid grid-cols-3 gap-3 border-t border-neutral-200 pt-6">
              <div className="flex flex-col items-center text-center gap-1">
                <Truck className="w-5 h-5 text-amber-600" />
                <span className="text-xs font-medium text-neutral-700">Livrare 24-48h</span>
              </div>
              <div className="flex flex-col items-center text-center gap-1">
                <ShieldCheck className="w-5 h-5 text-amber-600" />
                <span className="text-xs font-medium text-neutral-700">Garanție 24 luni</span>
              </div>
              <div className="flex flex-col items-center text-center gap-1">
                <RotateCcw className="w-5 h-5 text-amber-600" />
                <span className="text-xs font-medium text-neutral-700">Retur 14 zile</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16">
          <div className="flex items-center gap-6 border-b border-neutral-200">
            {[
              { id: 'desc', label: 'Descriere' },
              { id: 'info', label: 'Informații suplimentare' },
              { id: 'reviews', label: 'Recenzii (0)' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id ? 'border-amber-500 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="py-6 text-neutral-700 leading-relaxed max-w-3xl">
            {tab === 'desc' && <p>{product.description}</p>}
            {tab === 'info' && (
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b"><td className="py-2 font-medium">SKU</td><td>{product.sku}</td></tr>
                  <tr className="border-b"><td className="py-2 font-medium">Categorie</td><td>{product.categoryName}</td></tr>
                  <tr className="border-b"><td className="py-2 font-medium">Material</td><td>Piele naturală</td></tr>
                  <tr><td className="py-2 font-medium">Disponibilitate</td><td>În stoc</td></tr>
                </tbody>
              </table>
            )}
            {tab === 'reviews' && <p className="text-neutral-500">Nu există încă recenzii. Fii primul care evaluează acest produs.</p>}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-neutral-900 mb-6">Produse similare</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
